import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  Button, PageShell, SectionCard, FormSection, Input, RadixSelect, Switch, Skeleton,
  RequiredMark,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { useFaqQuery, useFaqMutation } from '../hooks/useFaqsQuery';
import { useFaqCategoriesQuery } from '../hooks/useFaqCategoriesQuery';
import type { FaqPayload, FaqTranslation } from '../types/faq';
import { LocaleHtmlTabs } from '@/features/cms/components/LocaleHtmlTabs';
import { type ContentLocale } from '@/features/cms/lib/contentLocales';
import { buildLocaleHtmlTranslations, pickLocaleHtml } from '@/features/cms/lib/localeContent';

// QA-122 — FAQ 답변 본문 per-locale 필드 키(FaqTranslation.answerHtml).
const FAQ_ANSWER_HTML_FIELD = 'answerHtml';

interface FaqFormState {
  categoryId: string;
  question: string;
  answerHtml: string;
  sortOrder: string;
  isActive: boolean;
  // QA-122 — locale 별 답변 HTML(en/ja/zh). ko-KR 은 answerHtml 정본. 빈 값 = SF ko 폴백.
  answerTranslations: Partial<Record<ContentLocale, string>>;
}

const EMPTY_FORM: FaqFormState = {
  categoryId: '',
  question: '',
  answerHtml: '',
  sortOrder: '0',
  isActive: true,
  answerTranslations: {},
};

export const FaqFormPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { faqId: faqIdParam } = useParams<{ faqId: string }>();
  const isNew = faqIdParam === 'new' || faqIdParam == null;
  const faqId = isNew ? null : Number(faqIdParam);

  const query = useFaqQuery(faqId);
  const categoriesQuery = useFaqCategoriesQuery();
  const { create, update, remove } = useFaqMutation();

  const [form, setForm] = useState<FaqFormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!query.data) return;
    setForm({
      categoryId: String(query.data.categoryId),
      question: query.data.question,
      answerHtml: query.data.answerHtml,
      sortOrder: String(query.data.sortOrder),
      isActive: query.data.isActive,
      answerTranslations: pickLocaleHtml(query.data.translations, FAQ_ANSWER_HTML_FIELD),
    });
  }, [query.data]);

  const set = <K extends keyof FaqFormState>(key: K, value: FaqFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setAnswerTranslation = (locale: ContentLocale, html: string) =>
    setForm((prev) => ({
      ...prev,
      answerTranslations: { ...prev.answerTranslations, [locale]: html },
    }));

  const categoryOptions = useMemo(
    () => (categoriesQuery.data ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    [categoriesQuery.data],
  );

  const canSubmit =
    form.categoryId !== '' && form.question.trim() !== '' && form.answerHtml.trim() !== '';

  const handleSubmit = async () => {
    setError(null);
    if (!canSubmit) {
      setError(t('faq.items.validation.required'));
      return;
    }
    const translations = buildLocaleHtmlTranslations<FaqTranslation>(
      form.answerTranslations,
      FAQ_ANSWER_HTML_FIELD,
    );
    const payload: FaqPayload = {
      categoryId: Number(form.categoryId),
      question: form.question.trim(),
      answerHtml: form.answerHtml,
      sortOrder: Number(form.sortOrder) || 0,
      isActive: form.isActive,
      ...(translations ? { translations } : {}),
    };
    try {
      if (isNew) {
        await create.mutateAsync(payload);
      } else if (faqId != null) {
        await update.mutateAsync({ faqId, payload });
      }
      navigate('/faq/items');
    } catch (e) {
      setError(friendlyMessage(e, t('faq.items.saveFailed')));
    }
  };

  const handleDelete = async () => {
    if (faqId == null) return;
    setError(null);
    try {
      await remove.mutateAsync(faqId);
      navigate('/faq/items');
    } catch (e) {
      setError(friendlyMessage(e, t('faq.items.deleteFailed')));
    }
  };

  const isBusy = create.isPending || update.isPending || remove.isPending;

  const breadcrumb = [
    { label: 'FAQ', href: '/faq/items' },
    { label: t('faq.items.title'), href: '/faq/items' },
    { label: isNew ? t('faq.create') : t('faq.edit') },
  ];

  if (!isNew && query.isPending) {
    return (
      <PageShell title={t('faq.items.editTitle')} breadcrumb={breadcrumb}>
        <div className="space-y-3" role="status" aria-live="polite">
          <Skeleton className="h-[44px] w-full" />
          <Skeleton className="h-[200px] w-full" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={isNew ? t('faq.items.createTitle') : t('faq.items.editTitle')}
      breadcrumb={breadcrumb}
      actions={
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={isBusy}>
              {t('faq.delete')}
            </Button>
          )}
          <Button size="sm" onClick={handleSubmit} disabled={isBusy || !canSubmit}>
            {t('faq.save')}
          </Button>
        </div>
      }
    >
      {error && (
        <div className="rounded-md p-3 text-sm" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
          {error}
        </div>
      )}

      <SectionCard title={t('faq.items.sectionBasic')}>
        <div className="flex flex-col gap-3">
          <FormSection label={t('faq.items.fields.category')} density="compact" required>
            <div className="max-w-[280px]">
              <RadixSelect
                value={form.categoryId}
                onChange={(v) => set('categoryId', v)}
                options={categoryOptions}
                placeholder={t('faq.items.fields.categoryPlaceholder')}
                aria-label={t('faq.items.fields.category')}
              />
            </div>
          </FormSection>
          <FormSection label={t('faq.items.fields.question')} density="compact" required>
            <Input
              value={form.question}
              onChange={(e) => set('question', e.target.value)}
              maxLength={500}
              required
            />
          </FormSection>
          <FormSection label={t('faq.items.fields.sortOrder')} density="compact">
            <Input
              type="number"
              value={form.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
              min={0}
              className="max-w-[160px]"
            />
          </FormSection>
          <FormSection label={t('faq.items.fields.isActive')} density="compact">
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => set('isActive', v)}
              aria-label={t('faq.items.fields.isActive')}
            />
          </FormSection>
        </div>
      </SectionCard>

      {/* QA-56 — 답변 본문은 저장 검증(canSubmit)이 ko 정본을 하드 강제하므로 필수 표기. */}
      <SectionCard title={<span>{t('faq.items.sectionAnswer')}<RequiredMark /></span>}>
        {/* QA-122 — 답변 본문을 로케일(ko/en/ja/zh)별로 입력. ko 정본, 타 로케일 빈 값 = SF ko 폴백. */}
        <LocaleHtmlTabs
          baseValue={form.answerHtml}
          onBaseChange={(html) => set('answerHtml', html)}
          translations={form.answerTranslations}
          onTranslationChange={setAnswerTranslation}
          ariaLabelPrefix={t('faq.items.fields.answer')}
        />
      </SectionCard>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('faq.items.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('faq.items.deleteDesc', { question: form.question })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('faq.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={isBusy}
              onClick={() => { setConfirmDelete(false); void handleDelete(); }}
            >
              {t('faq.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
